import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, IconButton, MenuItem, TextField, Tooltip } from '@mui/material';
import { useState } from 'react';
import { DEVICE_TYPES, MODEL_IMAGE_SLOTS, MODEL_TEXT_FIELDS, RECORD_STATUSES } from '@app/shared';
import type { ModelTextKey, ProductModelDto } from '@app/shared';
import { ColumnChooser, useColumnChoice } from '../../components/ColumnChooser';
import { DataTable, rowNumber } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { FilterPanel } from '../../components/FilterPanel';
import { PageHeader } from '../../components/PageHeader';
import { StatusChip } from '../../components/StatusChip';
import { formatCents, formatDate } from '../../lib/format';
import { useListState } from '../../lib/useListState';
import { useFeatureReleased } from '../../release';
import { CATALOGUE_IMAGES_FEATURE, Thumbnail, imageUrl, modelImagePath } from './CatalogueImages';
import { ModelFormDialog } from './ModelFormDialog';
import { useProductModels } from './api';

type ModelColumn = Column<ProductModelDto>;

const TEXT_HEADER = Object.fromEntries(MODEL_TEXT_FIELDS.map(([key, header]) => [key, header]));
/** A typed-text column of the catalogue. Long descriptions are cut in the list. */
const textColumn = (key: ModelTextKey): ModelColumn => ({
  key,
  header: TEXT_HEADER[key]!,
  render: (m) => {
    const value = m[key];
    return value.length > 60 ? (
      <Tooltip title={value}>
        <span>{value.slice(0, 60)}…</span>
      </Tooltip>
    ) : (
      value
    );
  },
});
const yesNo = (value: boolean) => (value ? 'Yes' : 'No');

/** The columns a list can show, in the order of the reference's column chooser. */
const COLUMNS: ModelColumn[] = [
  { key: 'modelId', header: 'Make Model ID', sortable: true, render: (m) => m.modelId },
  { key: 'make', header: 'Make', sortable: true, render: (m) => m.make },
  { key: 'model', header: 'Model', sortable: true, nowrap: true, render: (m) => m.model },
  { key: 'modelNumber', header: 'Model Number', nowrap: true, render: (m) => m.modelNumber },
  { key: 'carrier', header: 'Carrier', nowrap: true, render: (m) => m.carrier },
  textColumn('insurance'),
  textColumn('emi'),
  {
    key: 'inventoryThreshold',
    header: 'Inventory Threshold',
    align: 'right',
    render: (m) => m.inventoryThreshold ?? '',
  },
  {
    key: 'amountCents',
    header: 'Amount',
    sortable: true,
    align: 'right',
    render: (m) => formatCents(m.amountCents),
  },
  textColumn('planType'),
  textColumn('featuresDescription'),
  textColumn('specification'),
  textColumn('keyBenefits'),
  textColumn('productDetails'),
  { key: 'showOnWebsite', header: 'Show Model on Website', render: (m) => yesNo(m.showOnWebsite) },
  { key: 'networkType', header: 'Network Type', nowrap: true, render: (m) => m.networkType },
  { key: 'simType', header: 'SIM Type', nowrap: true, render: (m) => m.simType },
  textColumn('fccId'),
  textColumn('hac'),
  textColumn('wifi'),
  textColumn('dataSupport'),
  { key: 'os', header: 'OS', nowrap: true, render: (m) => m.os },
  {
    key: 'status',
    header: 'Status',
    sortable: true,
    render: (m) => <StatusChip status={m.status} />,
  },
  { key: 'grade', header: 'Grade', render: (m) => m.grade },
  textColumn('mRating'),
  textColumn('tRating'),
  textColumn('voiceOnly'),
  { key: 'sku', header: 'SKU', sortable: true, nowrap: true, render: (m) => m.sku },
  textColumn('hotspotCapable'),
  {
    key: 'csrEquipmentPhoneType',
    header: 'CSR Equipment Phone Type',
    render: (m) => yesNo(m.csrEquipmentPhoneType),
  },
  textColumn('bluetooth'),
  { key: 'ram', header: 'RAM', nowrap: true, render: (m) => m.ram },
  { key: 'storage', header: 'Inbuilt Memory', nowrap: true, render: (m) => m.storage },
  { key: 'screen', header: 'Screen', nowrap: true, render: (m) => m.screen },
  textColumn('versionName'),
  textColumn('talkTime'),
  { key: 'camera', header: 'Camera', nowrap: true, render: (m) => m.camera },
  {
    key: 'websiteEquipmentPhoneType',
    header: 'Website Equipment Phone Type',
    render: (m) => yesNo(m.websiteEquipmentPhoneType),
  },
  { key: 'color', header: 'Color', nowrap: true, render: (m) => m.color },
  { key: 'deviceType', header: 'Device Type', nowrap: true, render: (m) => m.deviceType ?? '' },
  textColumn('imeiType'),
  textColumn('discount'),
  textColumn('discountValidity'),
  {
    key: 'inventory',
    header: 'Inventory',
    align: 'right',
    render: (m) => m.inventory.toLocaleString('en-US'),
  },
  {
    key: 'createdAt',
    header: 'Created Date',
    sortable: true,
    nowrap: true,
    render: (m) => formatDate(m.createdAt),
  },
  { key: 'createdBy', header: 'Created By', render: (m) => m.createdBy?.loginId ?? '' },
  { key: 'updatedBy', header: 'Updated By', render: (m) => m.updatedBy?.loginId ?? '' },
  {
    key: 'updatedAt',
    header: 'Updated Date',
    sortable: true,
    nowrap: true,
    render: (m) => formatDate(m.updatedAt),
  },
];

/** "Image 1" to "Image 3", after Inventory as on the reference (release switch `catalogue-images`). */
const IMAGE_COLUMNS: ModelColumn[] = MODEL_IMAGE_SLOTS.map((slot) => ({
  key: `image${slot}`,
  header: `Image ${slot}`,
  render: (m: ProductModelDto) => {
    const stored = m.images.find((i) => i.slot === slot);
    return stored ? (
      <Thumbnail
        src={imageUrl(modelImagePath(m.id, slot), stored)}
        alt={`${m.make} ${m.model} image ${slot}`}
      />
    ) : (
      ''
    );
  },
}));
const inventoryAt = COLUMNS.findIndex((c) => c.key === 'inventory') + 1;
const COLUMNS_WITH_IMAGES = [
  ...COLUMNS.slice(0, inventoryAt),
  ...IMAGE_COLUMNS,
  ...COLUMNS.slice(inventoryAt),
];

/** Shown until someone picks their own columns: the page stays readable without scrolling far. */
const INITIAL = [
  'modelId',
  'make',
  'model',
  'amountCents',
  'networkType',
  'simType',
  'os',
  'status',
  'grade',
  'sku',
  'ram',
  'storage',
  'screen',
  'camera',
  'color',
  'deviceType',
  'inventory',
  'createdAt',
  'createdBy',
  'updatedBy',
  'updatedAt',
];

const YES_NO_FILTER = [
  ['', 'All'],
  ['true', 'Y'],
  ['false', 'N'],
] as const;

/** Manage Model (PC314): the reference's "Model List" with add, edit and its column chooser. */
export function ModelsPage() {
  const list = useListState(
    { q: '', modelId: '', csr: '', website: '', status: '', deviceType: '' },
    10,
    '-modelId',
  );
  const models = useProductModels(list.params);
  const [form, setForm] = useState<{ model?: ProductModelDto } | null>(null);
  const featureOn = useFeatureReleased();
  const offered = featureOn(CATALOGUE_IMAGES_FEATURE) ? COLUMNS_WITH_IMAGES : COLUMNS;
  const choice = useColumnChoice('models', offered, INITIAL);

  const columns: ModelColumn[] = [
    { key: '#', header: '#', render: (_m, i) => rowNumber(i, list.page, list.pageSize), width: 50 },
    {
      key: 'action',
      header: 'Action',
      render: (m) => (
        <Tooltip title="Update Model Details">
          <IconButton
            size="small"
            onClick={() => setForm({ model: m })}
            aria-label={`Edit model ${m.make} ${m.model}`}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    ...offered.filter((c) => choice.shown.has(c.key)),
  ];

  const select = (
    field: 'csr' | 'website',
    label: string,
    options: ReadonlyArray<readonly [string, string]>,
  ) => (
    <TextField
      select
      label={label}
      value={list.draft[field]}
      onChange={(e) => list.setField(field, e.target.value)}
    >
      {options.map(([value, text]) => (
        <MenuItem key={value} value={value}>
          {text}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <Box>
      <PageHeader
        title="Model List"
        code="PC314"
        actions={
          <>
            <ColumnChooser
              title="Model Selection Filter"
              columns={offered}
              shown={choice.shown}
              onSave={choice.save}
            />
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setForm({})}>
              Add Model
            </Button>
          </>
        }
      />
      <FilterPanel onSearch={list.search} onReset={list.reset}>
        <TextField
          label="Make/Model"
          value={list.draft.q}
          onChange={(e) => list.setField('q', e.target.value)}
        />
        <TextField
          label="Model ID"
          value={list.draft.modelId}
          onChange={(e) => list.setField('modelId', e.target.value.replace(/\D/g, ''))}
          slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 9 } }}
        />
        {select('csr', 'CSR equipment phone type', YES_NO_FILTER)}
        {select('website', 'Website equipment phone type', YES_NO_FILTER)}
        <TextField
          select
          label="Status"
          value={list.draft.status}
          onChange={(e) => list.setField('status', e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {RECORD_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Device Type"
          value={list.draft.deviceType}
          onChange={(e) => list.setField('deviceType', e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {DEVICE_TYPES.map((t) => (
            <MenuItem key={t} value={t}>
              {t}
            </MenuItem>
          ))}
        </TextField>
      </FilterPanel>

      <DataTable
        label="Model List"
        columns={columns}
        rows={models.data?.data}
        rowKey={(m) => m.id}
        loading={models.isFetching}
        error={models.error}
        onRetry={() => void models.refetch()}
        page={list.page}
        pageSize={list.pageSize}
        total={models.data?.total ?? 0}
        onPageChange={list.setPage}
        onPageSizeChange={list.setPageSize}
        pageSizeOptions={[10, 20, 50, 100]}
        sort={list.sort}
        onSortChange={list.setSort}
      />

      <ModelFormDialog open={Boolean(form)} model={form?.model} onClose={() => setForm(null)} />
    </Box>
  );
}
